// Pure camera helpers for the Moments capture flow. No React. The DOM types for camera zoom are
// not in the standard lib, so a few narrow casts are used where noted.
export type Facing = "environment" | "user";

export type ZoomCapability = { min: number; max: number; step: number };

export function cameraSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

// iOS breaks getUserMedia in installed/standalone PWA mode — detect it so we can fall back to the
// native file-capture input instead of showing a dead camera.
export function isIosStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const standaloneDisplay = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(standaloneDisplay || iosStandalone);
}

export async function getCameraStream(facingMode: Facing): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1080 },
      height: { ideal: 1920 }
    }
  });
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

// Android Chrome exposes an optical/hardware zoom range; iOS Safari does not.
export function probeZoomCapability(stream: MediaStream | null): ZoomCapability | null {
  const track = stream?.getVideoTracks?.()[0];
  if (!track || typeof track.getCapabilities !== "function") {
    return null;
  }
  const caps = track.getCapabilities() as MediaTrackCapabilities & {
    zoom?: { min?: number; max?: number; step?: number };
  };
  if (caps.zoom && typeof caps.zoom.max === "number") {
    return { min: caps.zoom.min ?? 1, max: caps.zoom.max, step: caps.zoom.step ?? 0.1 };
  }
  return null;
}

export async function applyNativeZoom(stream: MediaStream | null, zoom: number): Promise<boolean> {
  const track = stream?.getVideoTracks?.()[0];
  if (!track) {
    return false;
  }
  try {
    await track.applyConstraints({ advanced: [{ zoom } as unknown as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}

// Draw the current frame to a JPEG blob at full (rectangular) resolution. When a CSS/digital zoom
// is used (no native zoom), crop the centered region so the saved photo matches the framing.
export function captureFrameToBlob(
  video: HTMLVideoElement,
  opts: { cssZoom?: number; mirror?: boolean } = {}
): Promise<Blob> {
  const vw = video.videoWidth || 1080;
  const vh = video.videoHeight || 1920;
  const zoom = Math.max(1, opts.cssZoom ?? 1);
  const sw = vw / zoom;
  const sh = vh / zoom;
  const sx = (vw - sw) / 2;
  const sy = (vh - sh) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sw);
  canvas.height = Math.round(sh);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("canvas_unavailable"));
  }
  if (opts.mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("capture_failed"))), "image/jpeg", 0.92);
  });
}

export function blobToFile(blob: Blob, name = `moment-${Date.now()}.jpg`): File {
  return new File([blob], name, { type: blob.type || "image/jpeg" });
}

// Friendlier message per getUserMedia rejection.
export function describeCameraError(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Bạn chưa cho phép dùng camera. Hãy bật quyền camera cho trang này.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Không tìm thấy camera phù hợp trên máy này.";
    case "NotReadableError":
      return "Camera đang được ứng dụng khác sử dụng.";
    default:
      return "Không mở được camera. Thử dùng camera hệ thống bên dưới.";
  }
}
